import { useEffect, useRef } from 'react';

const DEFAULT_BROWSER_FONT_SIZE = 16; /** in pixels */
const BASE_FONT_SIZE = 10; /** desired base font size in pixels — gives 1rem = 10px */

/** Maximum width of the scaled content */
export const MAX_WIDTH = 428;

/** The width that scale ratio is calculated from */
const BASE_WIDTH_414 = 414;

/**
 * Calculates width of scaled content, taking into account both
 * base width and maximum width.
 */
export const calculateWidth = (width: number) =>
  MAX_WIDTH < width ? BASE_WIDTH_414 : width;

const calculateRatio = (width: number) => width / BASE_WIDTH_414;

/**
 * Sets html font-size so that 1rem = 10px at BASE_WIDTH_414,
 * scaling proportionally with viewport width (capped at MAX_WIDTH).
 *
 * Replaces the static 62.5% baseline — the formula yields 62.5% at
 * BASE_WIDTH_414 and scales around it on narrower/wider viewports.
 */
export function useUIKitFontScale() {
  const updateWidthTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    function updateFontSize() {
      const ratio = calculateRatio(
        calculateWidth(document.documentElement.offsetWidth),
      );
      document.documentElement.style.fontSize = `${
        (BASE_FONT_SIZE * ratio * 100) / DEFAULT_BROWSER_FONT_SIZE
      }%`;
    }

    function updateFontSizeThrottler() {
      if (!updateWidthTimeout.current) {
        updateWidthTimeout.current = setTimeout(() => {
          updateWidthTimeout.current = undefined;
          // Will execute at max 15fps
          updateFontSize();
        }, 66);
      }
    }

    updateFontSize();

    window.addEventListener('resize', updateFontSizeThrottler, false);
    window.addEventListener(
      'orientationchange',
      updateFontSizeThrottler,
      false,
    );

    return () => {
      window.removeEventListener('orientationchange', updateFontSizeThrottler);
      window.removeEventListener('resize', updateFontSizeThrottler);
    };
  }, []);
}
